import React, { useState } from 'react';
import { ChangeOrderWithDetails } from '../../services/change-order.service';

interface ChangeOrderDetailProps {
  order: ChangeOrderWithDetails;
  onApprove?: (id: string) => Promise<void>;
  onReject?: (id: string, reason: string) => Promise<void>;
  onExecute?: (id: string) => Promise<void>;
  onClose: () => void;
}

export function ChangeOrderDetail({ order, onApprove, onReject, onExecute, onClose }: ChangeOrderDetailProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      executed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      pending: '⏳ Pendiente',
      approved: '✅ Aprobado',
      rejected: '❌ Rechazado',
      executed: '📋 Ejecutado'
    };
    return labels[status] || status;
  };

  const getReasonLabel = (reason: string): string => {
    const labels: Record<string, string> = {
      design_change: 'Cambio de diseño',
      material_change: 'Cambio de materiales',
      scope_change: 'Cambio de alcance',
      unforeseen: 'Imprevisto'
    };
    return labels[reason] || reason;
  };

  const handleAction = async (action: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await action();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      alert('Por favor, ingresa un motivo de rechazo');
      return;
    }
    await handleAction(() => onReject!(order.id, rejectionReason));
    setShowRejectModal(false);
    setRejectionReason('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xl font-semibold">{order.description}</h4>
          <div className="flex items-center gap-3 mt-1">
            <span className={`text-sm px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
            <span className="text-sm text-gray-500">{getReasonLabel(order.reason)}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Creado:</span>
          <span className="ml-2">{new Date(order.createdAt).toLocaleString()}</span>
        </div>
        <div>
          <span className="text-gray-500">Solicitado por:</span>
          <span className="ml-2">{order.requestedByName || 'N/A'}</span>
        </div>
        {order.approvedByName && (
          <div>
            <span className="text-gray-500">Aprobado por:</span>
            <span className="ml-2">{order.approvedByName}</span>
          </div>
        )}
        {order.approvedAt && (
          <div>
            <span className="text-gray-500">Fecha aprobación:</span>
            <span className="ml-2">{new Date(order.approvedAt).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <h5 className="font-medium mb-3">Items</h5>
        <div className="space-y-2">
          {order.items?.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
              <div className="flex-1">
                <span className="font-medium">{item.description}</span>
                <span className="text-sm text-gray-500 ml-3">
                  {item.quantity} x ${item.unitPrice.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm px-2 py-0.5 rounded ${
                  item.type === 'addition' ? 'bg-green-100 text-green-700' :
                  item.type === 'deduction' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {item.type === 'addition' ? '+' : item.type === 'deduction' ? '-' : '±'}
                </span>
                <span className="font-medium">${(item.quantity * item.unitPrice).toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-3 pt-2 border-t border-gray-200">
          <div className="text-right">
            <span className="text-gray-500">Impacto total:</span>
            <span className={`ml-2 text-xl font-bold ${order.costImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${order.costImpact?.toFixed(2) || '0.00'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4 border-t border-gray-200">
        {order.status === 'pending' && onApprove && (
          <>
            <button
              onClick={() => handleAction(() => onApprove(order.id))}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              ✅ Aprobar
            </button>
            {onReject && (
              <button
                onClick={() => setShowRejectModal(true)}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                ❌ Rechazar
              </button>
            )}
          </>
        )}
        {order.status === 'approved' && onExecute && (
          <button
            onClick={() => handleAction(() => onExecute(order.id))}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            📋 Ejecutar
          </button>
        )}
      </div>

      {/* Modal de rechazo */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h4 className="text-lg font-bold mb-4">Motivo de Rechazo</h4>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Describe el motivo del rechazo..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleRejectSubmit}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}